import type { BrowserOptions, ErrorEvent } from '@sentry/react';

/**
 * Options passed to Sentry.replayIntegration.
 *
 * Why this is split out from App.tsx:
 *   Session Replay's underlying rrweb library throws cross-origin
 *   SecurityError when it tries to attach observers into iframes whose
 *   contentDocument it cannot read (Auth0 silent-auth, Stripe checkout,
 *   Telegram WebView, etc.). Setting block: ['iframe'] tells rrweb to
 *   ignore iframe contents entirely. See Sentry issue CHAT-MARITACA-Y
 *   (73 events / 8 users in 24h before this fix).
 */
export const replayIntegrationOptions = {
    maskAllInputs: true,
    maskAllText: false,
    blockAllMedia: false,
    unblock: ['.MuiModal-root', '.MuiDialog-root', '[role="dialog"]'],
    mask: ['input[type="password"]', 'input[type="email"]', '[data-replay-mask]'],
    block: ['iframe'],
};

/**
 * Error patterns to drop before they hit Sentry. These come from inside the
 * Sentry SDK or rrweb itself (not user-facing bugs), so capturing them only
 * inflates noise and quota.
 */
export const sentryIgnoreErrors: (string | RegExp)[] = [
    /Failed to read a named property 'Element' from 'Window'/,
];

/**
 * Stack-frame URL patterns that identify code we did not ship: browser
 * extensions injected into the page and Google's tag loader (gtag/js, loaded
 * by utils/googleAdsTracking). Errors that pass through these frames are not
 * fixable in this repo.
 *
 * Why not Sentry's built-in `denyUrls`: it only looks at the TOP frame of the
 * stack. In the prod event (CHAT-MARITACA-3V / 3T) gtag fires a conversion
 * POST to google.com, an ad-blocker extension's injected fetch wrapper
 * rejects it, and the rejection surfaces as an unhandled
 * "TypeError: Failed to fetch (www.google.com)" whose top frame is Sentry's
 * own fetch instrumentation inside OUR bundle — so denyUrls never matches.
 * We inspect every frame instead (see dropThirdPartyScriptEvents).
 */
export const thirdPartyScriptFramePatterns: RegExp[] = [
    /^(chrome|moz|safari|safari-web|ms-browser)-extension:\/\//,
    /\/gtag\/js(\?|$)/,
    /googletagmanager\.com\//,
];

type FrameLike = { filename?: string | null; abs_path?: string | null };

export const isThirdPartyScriptFrame = (frame: FrameLike): boolean => {
    const url = frame?.filename || frame?.abs_path || '';
    return url !== '' && thirdPartyScriptFramePatterns.some((re) => re.test(url));
};

/**
 * beforeSend hook: drop error events whose stack passes through a browser
 * extension or Google's tag loader (any frame, not just the top one). Events
 * with no exception frames are left untouched — there is nothing to judge by
 * and we would rather see them than miss a real crash.
 */
export const dropThirdPartyScriptEvents = (event: ErrorEvent): ErrorEvent | null => {
    const values = event?.exception?.values ?? [];
    for (const value of values) {
        const frames = value?.stacktrace?.frames ?? [];
        if (frames.some((f) => isThirdPartyScriptFrame(f))) return null;
    }
    return event;
};

/**
 * Normalise the build-time release tag injected through
 * REACT_APP_SENTRY_RELEASE so it can be passed to Sentry.init.
 *
 * The Dockerfile sets REACT_APP_SENTRY_RELEASE at build time and uploads
 * sourcemaps to Sentry tagged with the same value. The SDK must report
 * events with that exact release for sourcemap association to work — without
 * it, every event lands with release=null and stacks stay unsymbolicated.
 * (Necessary but NOT sufficient for CHAT-MARITACA-10: that event arrives
 * with no source URL and no stack at all, so sourcemaps cannot symbolicate
 * it regardless. See attachStacktrace in sentryInitOptions below.)
 *
 * Returns undefined (never the literal string "undefined") when no real tag
 * is available, so Sentry skips the release field instead of creating a
 * release literally named "undefined".
 */
export const buildSentryReleaseTag = (raw: string | undefined): string | undefined => {
    const trimmed = (raw ?? '').trim();
    if (!trimmed || trimmed === 'undefined') return undefined;
    return trimmed;
};

/**
 * Top-level Sentry.init options whose shape is testable independently of the
 * DSN / integrations array (which can only be constructed at runtime).
 */
export const sentryInitOptions: Pick<
    BrowserOptions,
    | 'sendDefaultPii'
    | 'tracesSampleRate'
    | 'replaysSessionSampleRate'
    | 'replaysOnErrorSampleRate'
    | 'enableLogs'
    | 'ignoreErrors'
    | 'release'
    | 'attachStacktrace'
    | 'beforeSend'
> = {
    sendDefaultPii: true,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    enableLogs: true,
    // Capture a stack even when an error arrives without one (e.g. a
    // window.onerror with no Error object). CHAT-MARITACA-10 lands with
    // url="undefined" and zero frames; a synthetic capture-site stack is
    // the only lead Sentry can give for it.
    attachStacktrace: true,
    ignoreErrors: sentryIgnoreErrors,
    // Extension / gtag frames anywhere in the stack → not our bug, drop it.
    beforeSend: dropThirdPartyScriptEvents,
    release: buildSentryReleaseTag(process.env.REACT_APP_SENTRY_RELEASE),
};

/**
 * Tag stamped on every handled (non-crash) error reported via
 * utils/reportError. Used both to filter these events in the Sentry UI and to
 * opt them out of Session Replay below.
 */
export const HANDLED_TAG = 'handled';

/**
 * beforeErrorSampling predicate for Session Replay (wired in App.tsx).
 *
 * replaysOnErrorSampleRate is 1.0, so by default EVERY error-type event that
 * reaches Sentry pulls a full session replay. Our handled-error instrumentation
 * (utils/reportError) can emit many low-severity `warning` events; recording a
 * replay for each would blow the Session Replay quota. Sentry's replay
 * error-mode triggers on any error-TYPE event regardless of level, so level
 * alone does not protect us — we explicitly opt handled events out of replay
 * here. The error event itself is still sent; only the replay is skipped.
 *
 *   returns true  -> Sentry may record/keep the replay for this event
 *   returns false -> skip replay for this event
 */
type ReplaySamplingEvent = { tags?: { [key: string]: unknown } };
export const shouldRecordReplayForEvent = (event: ReplaySamplingEvent): boolean => {
    const handled = event?.tags?.[HANDLED_TAG];
    return handled !== 'true' && handled !== true;
};
